import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import FloatingBulb from "@/components/FloatingBulb";
import { Reveal } from "@/components/motion";
import PercheSection from "@/components/home/PercheSection";
import ServicesTeaser from "@/components/home/ServicesTeaser";
import SectorsSection from "@/components/home/SectorsSection";
import SectorsStrip from "@/components/home/SectorsStrip";
import AudienceSection from "@/components/home/AudienceSection";
import FaqSection from "@/components/FaqSection";
import { FAQS, faqPageJsonLd } from "@/lib/faq";
import { useSeo } from "@/lib/seo";

const HOME_FAQ_SLUGS = [
  "consulente-unico-o-piu-professionisti",
  "contributi-fondo-perduto-pmi-campania",
  "differenza-commercialista-consulente-aziendale",
  "consulenza-per-startup-idea-a-business",
];
const HOME_FAQS = HOME_FAQ_SLUGS.map((s) => FAQS.find((f) => f.slug === s));

export default function Home() {
  useSeo({
    title: "Consulenza aziendale per PMI e startup in Campania | Idea 360",
    description:
      "Consulenza aziendale a 360° per PMI, microimprese e startup in Campania: finanza, controllo di gestione, finanza agevolata e bandi, marketing, HR, legale, lavoro, formazione e contabilità con un unico team operativo.",
    path: "/",
    jsonLd: faqPageJsonLd(HOME_FAQS),
  });

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 30% 20%, rgba(250,204,21,0.4) 0%, transparent 45%), radial-gradient(circle at 75% 80%, rgba(250,204,21,0.18) 0%, transparent 50%)",
          }}
          aria-hidden="true"
        />
        <FloatingBulb
          size={120}
          className="absolute top-1/2 -translate-y-1/2 right-[8%] hidden lg:block opacity-90"
          testId="hero-bulb"
        />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-12 pb-20 sm:pt-32 sm:pb-40 relative z-10">
          <div className="flex flex-col items-start max-w-4xl">
            <Reveal as="p" y={16} className="eyebrow mb-6 sm:mb-8" data-testid="hero-eyebrow">
              Napoli · Salerno · Caserta · Avellino · Benevento · Tutta Italia
            </Reveal>

            <Reveal
              as="h1"
              delay={0.1}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6"
              style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.03em" }}
              data-testid="hero-h1"
            >
              Consulenza aziendale a 360° per{" "}
              <span className="text-[#FACC15]">PMI e startup</span> in Campania
            </Reveal>

            <Reveal
              as="p"
              delay={0.2}
              className="text-lg sm:text-2xl text-white/75 max-w-2xl leading-snug mb-5"
              data-testid="hero-subtitle"
            >
              Il partner operativo e strategico che affianca la tua azienda.
            </Reveal>

            <Reveal
              as="p"
              delay={0.3}
              className="text-base sm:text-lg text-[#FACC15] italic mb-10 sm:mb-12"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="hero-slogan"
            >
              our idea, your business
            </Reveal>

            <Reveal delay={0.4} className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
              <Link
                to="/contatti"
                className="btn-yellow inline-flex items-center justify-center gap-2 group w-full sm:w-auto"
                data-testid="hero-cta-primary"
              >
                Richiedi una consulenza
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/servizi" className="btn-outline-yellow text-center w-full sm:w-auto" data-testid="hero-cta-secondary">
                Scopri i servizi
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <SectorsStrip />
      <PercheSection />
      <AudienceSection subtitle="Che tu abbia o meno un'idea non importa, ci pensiamo noi a realizzarla." />
      <ServicesTeaser />
      <SectorsSection />

      {/* CTA FINALE */}
      <section className="bg-[#FACC15] text-black py-16 sm:py-32" data-testid="home-cta-section">
        <Reveal className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6"
            style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.03em" }}
          >
            Pronto a far crescere
            <br />
            la tua idea?
          </h2>
          <p className="text-black/70 text-base sm:text-lg mb-8 sm:mb-10 max-w-xl mx-auto leading-relaxed">
            Raccontaci il tuo progetto. Ti rispondiamo entro 24 ore.
          </p>
          <Link
            to="/contatti"
            className="btn-black inline-flex items-center gap-2 text-base group"
            data-testid="home-cta-final"
          >
            Richiedi una consulenza
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      <FaqSection items={HOME_FAQS} compact title="Le domande che ci fanno più spesso" />
    </>
  );
}
